import { OrderSide } from '@shared-types';
import { calculateDesiredExpenseFromQuantityAndPrice } from '@tools';

/**
 * Quantity from desired order amount (مبلغ سفارش).
 * Buy: floor — Sell: ceil
 *
 * Buy:  Quantity = Value / (Price × (1 + BuyCommission))
 * Sell: Quantity = Value / (Price × (1 − SellCommission))
 */
export function calculateQuantityFromOrderValue(
	side: OrderSide,
	value: number,
	price: number,
	commission: number
): number {
	if (!price || isNaN(price) || price === 0 || !value) {
		return 0;
	}
	switch (side) {
		case OrderSide.Buy:
			return Math.floor(value / (price * (1 + commission)));
		case OrderSide.Sell:
			return Math.ceil(value / (price * (1 - commission)));
		default:
			return 0;
	}
}

/** Order amount (مبلغ) from quantity × price with commission. */
export function calculateOrderValueFromQuantity(
	side: OrderSide,
	quantity: number,
	price: number,
	commission: number
): number {
	return calculateDesiredExpenseFromQuantityAndPrice(side, quantity, price, commission);
}

/** Sell: percentage of assets → rounded quantity. */
export function calculateQuantityFromAssetPercent(assetQuantity: number, percent: number): number {
	if (assetQuantity <= 0 || percent <= 0) {
		return 0;
	}
	return Math.round(assetQuantity * (percent / 100));
}

/** Buy: percentage of buying power → amount. */
export function calculateValueFromBuyingPowerPercent(buyingPower: number, percent: number): number {
	if (buyingPower <= 0 || percent <= 0) {
		return 0;
	}
	return Math.round(buyingPower * (percent / 100));
}

/** Slider percent from amount relative to buying power (buy). */
export function percentFromBuyingPower(value: number, buyingPower: number): number {
	if (!buyingPower) {
		return 0;
	}
	return Math.min(100, Math.round((value / buyingPower) * 100));
}

/** Slider percent from quantity relative to assets (sell). */
export function percentFromAssets(quantity: number, assetQuantity: number): number {
	if (!assetQuantity) {
		return 0;
	}
	return Math.min(100, Math.round((quantity / assetQuantity) * 100));
}

/** Cumulative depth quantity from row 0 through clicked index (inclusive). */
export function calculateCumulativeDepthQuantity(
	quantities: ReadonlyArray<number | undefined>,
	depthIndex: number
): number {
	let total = 0;
	const last = Math.min(depthIndex, quantities.length - 1);
	for (let i = 0; i <= last; i++) {
		total += quantities[i] ?? 0;
	}
	return total;
}

/**
 * Formats a number with thousand separators (e.g. 1500000 → "1,500,000").
 *
 * Rounded first: every price and amount in this app is a whole rial, so a commission or
 * percent-derived figure must not surface its fraction here.
 */
export function formatWithSeparator(value: number): string {
	return Math.round(value)
		.toString()
		.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Parses a formatted / raw numeric string into a finite number. */
export function parseFormattedNumber(raw: string | number): number {
	const normalized = String(raw).replace(/,/g, '');
	const parsed = Number(normalized);
	return Number.isFinite(parsed) ? parsed : 0;
}

const COMPACT_UNITS = [
	{ threshold: 1_000_000_000, suffix: 'B' },
	{ threshold: 1_000_000, suffix: 'M' },
	{ threshold: 1_000, suffix: 'K' }
] as const;

export function formatCompactNumber(value: number): string {
	const rounded = Math.round(value);
	const unit = COMPACT_UNITS.find(({ threshold }) => Math.abs(rounded) >= threshold);
	if (!unit) {
		return formatWithSeparator(rounded);
	}
	// Truncated, never rounded: a bound must not be shown as a value outside it.
	const whole = Math.trunc(rounded / unit.threshold);
	const decimal = Math.trunc((Math.abs(rounded) % unit.threshold) / (unit.threshold / 10));
	return `${formatWithSeparator(whole)}.${decimal}${unit.suffix}`;
}
